import React, { useState, useEffect, useRef } from 'react'
import { Search, X, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { SearchResult } from '../../types'

interface SearchInputProps {
  placeholder?: string
  onSearch: (term: string) => Promise<SearchResult[]>
  onSelect: (result: SearchResult) => void
  value?: string
  onChange?: (value: string) => void
  debounceMs?: number
  minSearchLength?: number
  maxResults?: number
  isLoading?: boolean
  disabled?: boolean
  error?: string
  emptyMessage?: string
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = 'Pesquisar...',
  onSearch,
  onSelect,
  value = '',
  onChange,
  debounceMs = 300,
  minSearchLength = 2,
  maxResults = 10,
  isLoading = false,
  disabled = false,
  error,
  emptyMessage = 'Nenhum resultado encontrado'
}) => {
  const [searchTerm, setSearchTerm] = useState(value)
  const [results, setResults] = useState<SearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isTyping, setIsTyping] = useState(false)
  
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout>()

  // Sincronizar com prop value
  useEffect(() => {
    setSearchTerm(value)
    setIsTyping(false) // Quando value vem de fora, não é o usuário digitando
  }, [value])

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Só fazer busca se o usuário está digitando
    if (searchTerm.length >= minSearchLength && isTyping) {
      debounceRef.current = setTimeout(async () => {
        setIsSearching(true)
        try {
          const searchResults = await onSearch(searchTerm)
          setResults(searchResults.slice(0, maxResults))
          setIsOpen(true)
          setSelectedIndex(-1)
        } catch (error) {
          console.error('Erro na pesquisa:', error)
          setResults([])
        } finally {
          setIsSearching(false)
        }
      }, debounceMs)
    } else if (searchTerm.length < minSearchLength) {
      setResults([])
      setIsOpen(false)
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [searchTerm, onSearch, debounceMs, minSearchLength, maxResults, isTyping])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setIsTyping(true) // Marcar que usuário está digitando
    setSearchTerm(newValue)
    onChange?.(newValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : results.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          handleSelect(results[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  const handleSelect = (result: SearchResult) => {
    setIsTyping(false) // Marcar que não está digitando
    setSearchTerm(result.nome)
    setResults([])
    setIsOpen(false)
    setSelectedIndex(-1)
    onSelect(result)
    onChange?.(result.nome)
  }

  const clearSearch = () => {
    setIsTyping(false) // Limpar não é digitar
    setSearchTerm('')
    setResults([])
    setIsOpen(false)
    setSelectedIndex(-1)
    onChange?.('')
    inputRef.current?.focus()
  }

  return (
    <div className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Não reabrir automaticamente no foco
          }}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={clsx(
            'block w-full pl-10 pr-10 py-2 border rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'placeholder-gray-400 text-gray-900',
            'transition-colors duration-200',
            error
              ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
            (disabled || isLoading) && 'bg-gray-50 text-gray-500 cursor-not-allowed'
          )}
        />
        
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          {(isLoading || isSearching) && (
            <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
          )}
          {searchTerm && !isLoading && !isSearching && (
            <button
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-danger-600">{error}</p>
      )}

      {/* Dropdown de resultados */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {results.length > 0 ? (
            results.map((result, index) => (
              <button
                key={result.id}
                onClick={() => handleSelect(result)}
                className={clsx(
                  'w-full text-left px-3 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                  'border-b border-gray-100 last:border-b-0',
                  selectedIndex === index && 'bg-primary-50'
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">
                      {result.nome}
                    </div>
                    {result.subtitle && (
                      <div className="text-sm text-gray-600">
                        {result.subtitle}
                      </div>
                    )}
                  </div>
                  {result.tipo && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {result.tipo}
                    </span>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              {isSearching ? 'A pesquisar...' : emptyMessage}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchInput